// Ligar e desligar as notificações push neste aparelho (IDEA-044).
//
// Três partes têm de estar todas de acordo para chegar uma notificação ao
// telemóvel, e é por isso que este ficheiro devolve um ESTADO e não um
// booleano:
//
//   1. o browser sabe fazer push (`PushManager` + service worker);
//   2. a pessoa deu autorização;
//   3. este aparelho está inscrito no nosso servidor.
//
// As três falham de maneiras diferentes e só uma se resolve com um botão. A
// pior é a segunda: uma autorização NEGADA não se pode voltar a pedir — o
// browser deixa de mostrar a caixa e `requestPermission()` devolve "denied"
// sem sequer perguntar. A única saída é ir às definições do site, e a página
// tem de o dizer em vez de oferecer um botão que já não faz nada.

import { subscribeToPush, unsubscribeFromPush } from "@/api/mutations";

export type EstadoPush =
  /** Este browser não sabe fazer push. */
  | "sem-suporte"
  /** Autorização negada — só se resolve nas definições do browser. */
  | "negado"
  /** Pode-se ligar. */
  | "desligado"
  /** Ligado neste aparelho. */
  | "ligado";

/** O browser sabe fazer isto? */
export function suportado(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/**
 * A chave pública VAPID vem em base64url e o browser quer-na em bytes.
 *
 * base64url usa `-` e `_` onde o base64 normal usa `+` e `/`, e dispensa o
 * enchimento com `=`. `atob` não sabe nada disso, por isso a conversão é feita
 * aqui — passar-lhe a chave tal como vem falha com um erro de tamanho que não
 * diz o que se passou.
 */
function chaveParaBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const enchimento = "=".repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + enchimento).replace(/-/g, "+").replace(/_/g, "/");
  const bruto = window.atob(base64);
  // Sobre um `ArrayBuffer` explícito: `new Uint8Array(n)` é
  // `Uint8Array<ArrayBufferLike>` desde o TypeScript 5.7, e `applicationServerKey`
  // não aceita um buffer que possa ser partilhado.
  const bytes = new Uint8Array(new ArrayBuffer(bruto.length));
  for (let i = 0; i < bruto.length; i += 1) bytes[i] = bruto.charCodeAt(i);
  return bytes;
}

/**
 * O estado actual, sem pedir nada nem mexer em nada.
 *
 * `subscritoNoServidor` vem da API — a inscrição existe dos dois lados e as
 * duas podem divergir (limpar os dados do site apaga a do browser e deixa a
 * nossa para trás). Manda a do browser: é a que decide se chega alguma coisa.
 */
export async function estadoActual(subscritoNoServidor: boolean): Promise<EstadoPush> {
  if (!suportado()) return "sem-suporte";
  if (Notification.permission === "denied") return "negado";

  const registo = await navigator.serviceWorker.getRegistration();
  const inscricao = await registo?.pushManager.getSubscription();

  // Sem inscrição no browser não chega nada, por muito que o servidor tenha
  // uma linha guardada — daí o `&&` e não um `||`.
  return inscricao && subscritoNoServidor ? "ligado" : "desligado";
}

/**
 * Liga as notificações neste aparelho.
 *
 * `requestPermission()` tem de sair de um toque — os browsers ignoram-no fora
 * de um gesto do utilizador —, por isso isto chama-se do `onClick` e de mais
 * lado nenhum.
 */
export async function ligar(chavePublica: string): Promise<EstadoPush> {
  if (!suportado()) return "sem-suporte";

  const permissao = await Notification.requestPermission();
  if (permissao !== "granted") return permissao === "denied" ? "negado" : "desligado";

  // `ready` e não `getRegistration()`: à primeira visita o service worker pode
  // ainda estar a instalar, e inscrever-se num registo que ainda não está
  // activo falha.
  const registo = await navigator.serviceWorker.ready;

  // Reaproveitar a inscrição que já exista. Pedir outra com a mesma chave
  // devolve a mesma na mesma, mas pedir com uma chave DIFERENTE rebenta com um
  // erro — e é isso que acontece se o par VAPID do servidor for trocado.
  const existente = await registo.pushManager.getSubscription();
  const inscricao =
    existente ??
    (await registo.pushManager.subscribe({
      // Sem isto o Chrome recusa: uma push silenciosa não é permitida, e toda a
      // notificação nossa mostra alguma coisa de qualquer maneira.
      userVisibleOnly: true,
      applicationServerKey: chaveParaBytes(chavePublica),
    }));

  // `toJSON()` aninha as chaves; o servidor guarda-as achatadas.
  const json = inscricao.toJSON() as {
    endpoint?: string;
    keys?: { p256dh?: string; auth?: string };
  };
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) {
    throw new Error("O browser devolveu uma inscrição incompleta.");
  }

  await subscribeToPush({
    endpoint: json.endpoint,
    p256dh: json.keys.p256dh,
    auth: json.keys.auth,
  });

  return "ligado";
}

/**
 * Desliga neste aparelho: apaga a inscrição no browser e a linha no servidor.
 *
 * As duas, e por esta ordem. Só apagar a nossa deixaria o browser inscrito num
 * servidor que já não lhe manda nada; só apagar a do browser deixaria a linha
 * a falhar em todos os envios até um 410 a varrer.
 */
export async function desligar(): Promise<EstadoPush> {
  if (!suportado()) return "sem-suporte";

  const registo = await navigator.serviceWorker.getRegistration();
  const inscricao = await registo?.pushManager.getSubscription();

  if (inscricao) {
    const endpoint = inscricao.endpoint;
    await inscricao.unsubscribe();
    // Se esta falhar, o 410 do serviço de push limpa-a no primeiro envio — daí
    // não valer a pena desfazer o que já foi feito acima.
    await unsubscribeFromPush(endpoint).catch(() => undefined);
  }

  return "desligado";
}
